/**
 * The two extra reads the Activity tab needs, on top of what the property page
 * has already loaded.
 *
 * Everything else the feed shows (tenancies, past tenants, tenant documents) is
 * already in hand from useOwnerPropertyDetail / useOwnerPropertyDocuments, so
 * asking for it again would be a second copy of the same rows that can disagree
 * with the first. Only these two are missing:
 *
 *   1. The submission lifecycle columns on `properties`. That table's SELECT
 *      policy is USING (true), so this is a direct query, not an RPC.
 *   2. Documents INCLUDING soft-deleted rows — every other document query filters
 *      `deleted_at IS NULL`, but a removal is exactly the kind of thing a
 *      timeline exists to record.
 *
 * Both are gated on the tab being open: an owner who never opens Activity pays
 * for neither.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  withUploaderNames,
  type ApartmentDocument,
} from "@/hooks/v2/useApartmentDocuments";
import type { ActivityPropertyRow } from "@/lib/owner-property-activity";

/** Submission lifecycle for one property — what the feed dates its events by. */
export function useOwnerPropertySubmissionRow(
  propertyId: number | null,
  enabled: boolean
) {
  return useQuery({
    queryKey: ["owner-property-submission-row", propertyId],
    enabled: !!propertyId && enabled,
    queryFn: async (): Promise<ActivityPropertyRow | null> => {
      const { data, error } = await supabase
        .from("properties")
        .select(
          "created_at, submitted_at, reviewed_at, approval_status, review_note, withdrawn_at, withdraw_reason"
        )
        .eq("id", propertyId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as ActivityPropertyRow | null;
    },
  });
}

/**
 * Every document row for the property at every level, soft-deleted ones
 * included, with uploader names resolved.
 *
 * Deliberately its own query rather than a flag on
 * usePropertyApartmentDocuments: that hook backs the lists, where a deleted
 * document must never appear, and a boolean that makes a list leak removed
 * files is one wrong argument away at every call site.
 */
export function useOwnerPropertyDocumentHistory(
  propertyId: number | null,
  enabled: boolean
) {
  return useQuery({
    queryKey: ["owner-property-document-history", propertyId],
    enabled: !!propertyId && enabled,
    queryFn: async (): Promise<ApartmentDocument[]> => {
      const { data, error } = await supabase
        .from("apartment_documents")
        .select("*")
        .eq("property_id", propertyId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return withUploaderNames(
        (data ?? []) as Array<Omit<ApartmentDocument, "uploader_name">>
      );
    },
  });
}
